<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { BUILDING_LEVEL_LABEL } from "@modules/usage-profile/constants"
import type { UsageProfileBuildingFormatted } from "@modules/usage-profile/types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            heading: "Bâtiments utilisant le profil {name}",
            building: "Bâtiments",
            edit: "Modifier",
            level: "Catégorie",
            name: "Nom",
            address: "Adresse",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Identifiant du profil */
        profileId: string
        /** Nom du profil */
        name: string
        /** Liste des bâtiments associés */
        buildings?: UsageProfileBuildingFormatted[]
        /** Mode lecture seule */
        readonly?: boolean
    }>(),
    { buildings: () => [], readonly: false }
)

defineEmits<{ "click:edit": [] }>()

// ================================
// DONNÉES CALCULÉES
// ================================
/** Phrase récapitulative (X groupes • Y bâtiments • Z parties) */
const getValueSentence = computed(() => {
    const groupe = props.buildings.filter((b) => b.level === "groupe").length
    const building = props.buildings.filter(
        (b) =>
            b.level &&
            !["groupe", "appartement", "maison_appartement"].includes(b.level)
    ).length
    const part = props.buildings.filter((b) =>
        ["appartement", "maison_appartement"].includes(b.level || "")
    ).length
    const parts: string[] = []
    if (groupe) parts.push(`${groupe} groupe${groupe > 1 ? "s" : ""}`)
    if (building) parts.push(`${building} bâtiment${building > 1 ? "s" : ""}`)
    if (part) parts.push(`${part} partie${part > 1 ? "s" : ""}`)
    return parts.join(" • ") || "-"
})

/** Identifiant du collapse (si bâtiments) */
const getCollapseId = computed(() =>
    props.buildings.length > 0 ? `collapse-building-${props.profileId}` : ""
)
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT -->
    <!-- ================================ -->
    <div class="usage-profile-item-building-list">
        <UsageProfileItemField
            class="usage-profile-item-building-list__header min-h-10 px-3 border-b border-gray-200"
            :label="t('heading', { name })"
            label-class="text-primary-700! text-base! font-semibold!"
        >
            <BaseButton
                v-if="!readonly"
                :label="t('edit')"
                size="small"
                text
                @click="$emit('click:edit')"
            />
        </UsageProfileItemField>
        <UsageProfileItemField
            class="border-b border-gray-200"
            :label="t('building')"
            :value="getValueSentence"
            :collapse-id="getCollapseId"
        >
            <template #collapse>
                <div class="flex flex-col pt-6 overflow-y-auto max-h-[27.5rem]">
                    <div
                        class="grid grid-cols-[7.5rem_minmax(auto,17rem)_minmax(20rem,1fr)] items-center"
                    >
                        <p
                            class="overflow-hidden text-ellipsis p-2 text-xs font-semibold text-primary-700"
                        >
                            {{ t("level") }}
                        </p>
                        <p
                            class="overflow-hidden text-ellipsis p-2 text-xs font-semibold text-primary-700"
                        >
                            {{ t("name") }}
                        </p>
                        <p
                            class="overflow-hidden text-ellipsis p-2 text-xs font-semibold text-primary-700"
                        >
                            {{ t("address") }}
                        </p>
                    </div>
                    <div
                        v-for="(field, index) in buildings"
                        :key="index"
                        class="grid grid-cols-[7.5rem_minmax(auto,17rem)_minmax(20rem,1fr)] items-center even:bg-primary-50 odd:bg-primary-100"
                    >
                        <div class="overflow-hidden text-ellipsis p-2 text-sm">
                            <span
                                class="block overflow-hidden text-ellipsis whitespace-nowrap text-sm text-surface-700"
                                :title="
                                    BUILDING_LEVEL_LABEL[field.level || ''] ||
                                    field.level
                                "
                            >
                                {{
                                    BUILDING_LEVEL_LABEL[field.level || ""] ||
                                    field.level ||
                                    "-"
                                }}
                            </span>
                        </div>
                        <div class="overflow-hidden text-ellipsis p-2 text-sm">
                            <span
                                class="block truncate text-sm text-surface-700"
                                :title="field.name"
                                >{{ field.name }}</span
                            >
                        </div>
                        <div class="overflow-hidden text-ellipsis p-2 text-sm">
                            <span
                                class="block truncate text-sm text-surface-700"
                                :title="field.address || ''"
                            >
                                {{ field.address || "-" }}
                            </span>
                        </div>
                    </div>
                </div>
            </template>
        </UsageProfileItemField>
    </div>
</template>
